import { flattenedSize, inverseProjection, rotateQuad, type Quad } from './geometry.js';

/**
 * SCAN-003 — turning the photograph into a page.
 *
 * Four things happen here, and they are the difference between a photograph of
 * a document and a scan of one: the perspective is undone so the page is a
 * rectangle, the lighting is divided out so the paper is white everywhere
 * rather than white on one side and grey on the other, the contrast is opened
 * up, and the page is turned the right way up.
 *
 * All of it on the GPU, one pass over the pixels each. The same arithmetic
 * written as loops takes a second or two per page on a phone, and a scan is as
 * long as somebody's stack of paper — minutes of spinner and a hot handset, for
 * work a graphics card does in a frame. `renderOnCpu` at the bottom is that
 * loop, kept for a device with no WebGL at all, and deliberately cruder.
 */

/** What the page should look like when it comes out. */
export type ScanFilter =
  /** The photograph, straightened and no more. Right for a colour receipt. */
  | 'original'
  /** Straightened, lit evenly, contrast opened. The everyday choice. */
  | 'colour'
  /** The same, then driven to black on white. Smallest, and the best for OCR. */
  | 'mono';

export interface ScanEdit {
  /** The page's corners, in the photograph's own pixels. */
  quad: Quad;
  filter: ScanFilter;
  /** Quarter-turns clockwise. */
  rotation: number;
  /**
   * Longest side of the output, in pixels.
   *
   * The same page is rendered at two sizes for two purposes: a few hundred
   * pixels for the thumbnail in the review grid, which has to redraw for all
   * twenty pages the instant a filter is chosen, and full size once, at the end,
   * for the PDF. Rendering the thumbnails at page size is the difference
   * between a filter that previews instantly and one that takes two seconds to
   * answer a tap.
   */
  maxSize?: number;
}

type Source = ImageBitmap | HTMLImageElement | HTMLCanvasElement;

const VERTEX = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

/**
 * The straightening pass.
 *
 * Every pixel of the output asks the matrix which pixel of the photograph it
 * came from, which is why the matrix runs backwards — see `inverseProjection`.
 * Sampling is the hardware's own bilinear, which is much of the reason this is
 * worth doing on a GPU at all: the same interpolation by hand is four reads and
 * six multiplies per pixel, in a language with no vector types.
 */
const WARP = `
precision highp float;
uniform sampler2D u_photo;
uniform mat3 u_projection;
uniform vec2 u_output;
uniform vec2 u_photoSize;
varying vec2 v_uv;
void main() {
  vec2 pixel = vec2(v_uv.x, 1.0 - v_uv.y) * u_output;
  vec3 mapped = u_projection * vec3(pixel, 1.0);
  gl_FragColor = texture2D(u_photo, (mapped.xy / mapped.z) / u_photoSize);
}`;

/** A half-size copy, averaged four pixels at a time. Run repeatedly, this is
 *  how a blur wide enough to be a shadow gets built out of cheap small ones. */
const HALVE = `
precision mediump float;
uniform sampler2D u_source;
uniform vec2 u_texel;
varying vec2 v_uv;
void main() {
  gl_FragColor = 0.25 * (
    texture2D(u_source, v_uv + vec2(-0.5, -0.5) * u_texel) +
    texture2D(u_source, v_uv + vec2( 0.5, -0.5) * u_texel) +
    texture2D(u_source, v_uv + vec2(-0.5,  0.5) * u_texel) +
    texture2D(u_source, v_uv + vec2( 0.5,  0.5) * u_texel));
}`;

/** One direction of a nine-tap gaussian, its five weights folded into three
 *  reads by letting the hardware interpolate between each neighbouring pair. */
const BLUR = `
precision mediump float;
uniform sampler2D u_source;
uniform vec2 u_step;
varying vec2 v_uv;
void main() {
  vec4 sum = texture2D(u_source, v_uv) * 0.2270270270;
  sum += (texture2D(u_source, v_uv + u_step * 1.3846153846) +
          texture2D(u_source, v_uv - u_step * 1.3846153846)) * 0.3162162162;
  sum += (texture2D(u_source, v_uv + u_step * 3.2307692308) +
          texture2D(u_source, v_uv - u_step * 3.2307692308)) * 0.0702702703;
  gl_FragColor = sum;
}`;

/**
 * The pass that makes it look scanned.
 *
 * `u_lighting` is the page blurred until nothing of the text survives, which
 * leaves a picture of the light that fell on it — the lamp's bright patch, the
 * photographer's own shadow, the grey gradient across a page held near a
 * window. Dividing the page by it asks "how dark is this pixel *compared with
 * the paper beside it*", and that question has the same answer on both sides of
 * a shadow. The one division is the shadow removal; the rest is contrast.
 *
 * The levels that follow can be as blunt as they are only because the division
 * has already happened. Applied to the raw photograph, a fixed black point
 * crushes the shadowed half of the page into a solid block — which is precisely
 * what a scanner app that skips this step does to a document held in one hand.
 */
const FINISH = `
precision highp float;
uniform sampler2D u_page;
uniform sampler2D u_lighting;
uniform float u_mono;
varying vec2 v_uv;
void main() {
  vec3 page = texture2D(u_page, v_uv).rgb;
  vec3 lighting = texture2D(u_lighting, v_uv).rgb;
  vec3 evened = clamp(page / max(lighting, vec3(0.06)), 0.0, 1.0);

  vec3 levelled = clamp((evened - 0.16) / 0.74, 0.0, 1.0);

  float ink = dot(evened, vec3(0.299, 0.587, 0.114));
  // Not a hard step: a threshold with no ramp turns every letter's edge into a
  // staircase, and at the size text is printed the staircase is the first thing
  // the eye finds.
  float bilevel = smoothstep(0.52, 0.78, ink);

  gl_FragColor = vec4(mix(levelled, vec3(bilevel), u_mono), 1.0);
}`;

function compile(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('scan: no shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(`scan: shader failed — ${gl.getShaderInfoLog(shader) ?? ''}`);
  }
  return shader;
}

function link(gl: WebGLRenderingContext, fragment: string): WebGLProgram {
  const created = gl.createProgram();
  if (!created) throw new Error('scan: no program');
  gl.attachShader(created, compile(gl, gl.VERTEX_SHADER, VERTEX));
  gl.attachShader(created, compile(gl, gl.FRAGMENT_SHADER, fragment));
  gl.linkProgram(created);
  if (!gl.getProgramParameter(created, gl.LINK_STATUS)) {
    throw new Error(`scan: link failed — ${gl.getProgramInfoLog(created) ?? ''}`);
  }
  return created;
}

interface Renderer {
  canvas: HTMLCanvasElement;
  gl: WebGLRenderingContext;
  corners: WebGLBuffer;
  warp: WebGLProgram;
  halve: WebGLProgram;
  blur: WebGLProgram;
  finish: WebGLProgram;
}

/**
 * One canvas, one context, four programs — made once and kept.
 *
 * A browser allows something like sixteen live WebGL contexts and drops the
 * oldest without asking when a seventeenth is made. A scan has no page limit,
 * so a context per page is not a wasteful version of this: it is a feature that
 * stops working from page seventeen, on a device slow enough that nobody would
 * think to blame the context count.
 *
 * Null means this device has no WebGL and never will; the caller falls back.
 */
let renderer: Renderer | null | undefined;

function acquire(): Renderer | null {
  if (renderer !== undefined) return renderer;

  const canvas = document.createElement('canvas');
  const gl = (canvas.getContext('webgl', { preserveDrawingBuffer: true, antialias: false }) ??
    canvas.getContext('experimental-webgl', {
      preserveDrawingBuffer: true,
    })) as WebGLRenderingContext | null;

  if (!gl) {
    renderer = null;
    return null;
  }

  const corners = gl.createBuffer();
  if (!corners) {
    renderer = null;
    return null;
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, corners);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

  try {
    renderer = {
      canvas,
      gl,
      corners,
      warp: link(gl, WARP),
      halve: link(gl, HALVE),
      blur: link(gl, BLUR),
      finish: link(gl, FINISH),
    };
  } catch {
    // A context that will not compile these is a context that cannot do the
    // job, and saying so once is better than throwing on every page.
    renderer = null;
  }
  return renderer;
}

/**
 * A texture to draw into, and the framebuffer that points at it.
 *
 * Clamped and unmipped because these are never powers of two — a page is
 * 1654×2339, not 2048×2048 — and WebGL 1 refuses to sample a non-power-of-two
 * texture that repeats or carries mipmaps. Linear filtering is still allowed,
 * and is what lets the small blurred copy be read back at full size for free.
 */
function target(gl: WebGLRenderingContext, width: number, height: number) {
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  const framebuffer = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
  return { texture, framebuffer, width, height };
}

type Target = ReturnType<typeof target>;

const sourceWidth = (source: Source): number =>
  source instanceof HTMLImageElement ? source.naturalWidth : source.width;
const sourceHeight = (source: Source): number =>
  source instanceof HTMLImageElement ? source.naturalHeight : source.height;

export function hasWebgl(): boolean {
  return acquire() !== null;
}

export type RenderPath = 'webgl' | 'cpu';

/**
 * The photograph, straightened, evened out and turned — as an image file.
 *
 * The rotation is applied by renaming the corners rather than by turning any
 * pixels; see `rotateQuad`. Everything downstream, the output's proportions
 * included, follows from the corner order, so a quarter-turn costs four array
 * reads.
 */
export async function renderPage(source: Source, edit: ScanEdit): Promise<{ blob: Blob; path: RenderPath }> {
  const quad = rotateQuad(edit.quad, edit.rotation);
  const size = flattenedSize(quad, edit.maxSize ?? 2200);

  const active = acquire();
  if (!active) return { blob: await renderOnCpu(source, quad, size), path: 'cpu' };
  const { gl, canvas } = active;

  const projection = inverseProjection(quad, size.width, size.height);
  if (!projection) throw new Error('scan: those corners do not make a page');

  canvas.width = size.width;
  canvas.height = size.height;

  // Everything made for this page, so it can all be released at the end of it.
  const spent: Target[] = [];
  const photo = gl.createTexture();

  const draw = (shader: WebGLProgram, into: Target | null) => {
    gl.useProgram(shader);
    const position = gl.getAttribLocation(shader, 'a_position');
    gl.bindBuffer(gl.ARRAY_BUFFER, active.corners);
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, into ? into.framebuffer : null);
    gl.viewport(0, 0, into ? into.width : size.width, into ? into.height : size.height);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };

  const bind = (shader: WebGLProgram, name: string, texture: WebGLTexture | null, unit: number) => {
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform1i(gl.getUniformLocation(shader, name), unit);
  };

  try {
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, photo);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source as TexImageSource);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const flat = edit.filter === 'original' ? null : target(gl, size.width, size.height);
    if (flat) spent.push(flat);

    const { warp } = active;
    gl.useProgram(warp);
    bind(warp, 'u_photo', photo, 0);
    gl.uniformMatrix3fv(gl.getUniformLocation(warp, 'u_projection'), false, projection);
    gl.uniform2f(gl.getUniformLocation(warp, 'u_output'), size.width, size.height);
    gl.uniform2f(gl.getUniformLocation(warp, 'u_photoSize'), sourceWidth(source), sourceHeight(source));
    draw(warp, flat);

    if (flat) {
      // Six halvings put the page into a thumbnail, and a page's own text is
      // gone by then — only the light that fell on it survives at that size.
      // Blurring there and reading it back magnified costs a sixty-fourth of
      // blurring at full size, and a shadow has no detail worth the difference.
      let small: Target = flat;
      for (let step = 0; step < 6; step += 1) {
        const next = target(gl, Math.max(4, small.width >> 1), Math.max(4, small.height >> 1));
        spent.push(next);
        gl.useProgram(active.halve);
        bind(active.halve, 'u_source', small.texture, 0);
        gl.uniform2f(gl.getUniformLocation(active.halve, 'u_texel'), 1 / small.width, 1 / small.height);
        draw(active.halve, next);
        small = next;
      }

      for (const [dx, dy] of [
        [1, 0],
        [0, 1],
      ] as const) {
        const next = target(gl, small.width, small.height);
        spent.push(next);
        gl.useProgram(active.blur);
        bind(active.blur, 'u_source', small.texture, 0);
        gl.uniform2f(gl.getUniformLocation(active.blur, 'u_step'), dx / small.width, dy / small.height);
        draw(active.blur, next);
        small = next;
      }

      gl.useProgram(active.finish);
      bind(active.finish, 'u_page', flat.texture, 0);
      bind(active.finish, 'u_lighting', small.texture, 1);
      gl.uniform1f(gl.getUniformLocation(active.finish, 'u_mono'), edit.filter === 'mono' ? 1 : 0);
      draw(active.finish, null);
    }

    return { blob: await encode(canvas, edit.filter), path: 'webgl' };
  } finally {
    // A page's worth of intermediates is tens of megabytes of graphics memory,
    // and the context outlives the page — so nothing here waits for a collector
    // that has no idea any of it exists.
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    for (const used of spent) {
      gl.deleteFramebuffer(used.framebuffer);
      gl.deleteTexture(used.texture);
    }
    gl.deleteTexture(photo);
  }
}

/**
 * A page as a file.
 *
 * Black-and-white goes out as PNG and everything else as JPEG, which is the
 * reverse of the usual advice and right here for one reason: JPEG stores
 * frequencies, and a letter's edge is the highest frequency on the page. It
 * comes back ringed with grey speckle — visible on screen, and the sort of
 * thing OCR reads as punctuation. A page that is mostly two colours is also
 * exactly what PNG's compression is best at, so it comes out smaller as well.
 */
function encode(canvas: HTMLCanvasElement, filter: ScanFilter): Promise<Blob> {
  const type = filter === 'mono' ? 'image/png' : 'image/jpeg';
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('scan: the page would not encode'))),
      type,
      0.9,
    );
  });
}

/**
 * The same straightening without a GPU, for a device that has no WebGL.
 *
 * Straightening only: no lighting, no levels, no black-and-white. Those are
 * three more passes over every pixel, and this one already walks millions of
 * them in a language that does them one at a time. A crooked page nobody can
 * read is a failure; a page that is merely a photograph is not.
 */
async function renderOnCpu(
  source: Source,
  quad: Quad,
  size: { width: number; height: number },
): Promise<Blob> {
  const photo = document.createElement('canvas');
  photo.width = sourceWidth(source);
  photo.height = sourceHeight(source);
  const from = photo.getContext('2d');
  if (!from) throw new Error('scan: no canvas');
  from.drawImage(source as CanvasImageSource, 0, 0);
  const pixels = from.getImageData(0, 0, photo.width, photo.height).data;

  // Half size, because every pixel below is sampled by hand.
  const width = Math.max(1, size.width >> 1);
  const height = Math.max(1, size.height >> 1);
  const projection = inverseProjection(quad, width, height);
  if (!projection) throw new Error('scan: those corners do not make a page');

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const into = canvas.getContext('2d');
  if (!into) throw new Error('scan: no canvas');
  const out = into.createImageData(width, height);

  // Column-major, as `inverseProjection` documents: reading it in rows silently
  // transposes the matrix, and a transposed homography is still a homography —
  // it just maps the page somewhere else, and the scan comes out as a smear.
  const m0 = projection[0] as number;
  const m1 = projection[1] as number;
  const m2 = projection[2] as number;
  const m3 = projection[3] as number;
  const m4 = projection[4] as number;
  const m5 = projection[5] as number;
  const m6 = projection[6] as number;
  const m7 = projection[7] as number;
  const m8 = projection[8] as number;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const w = m2 * x + m5 * y + m8;
      const sx = (m0 * x + m3 * y + m6) / w;
      const sy = (m1 * x + m4 * y + m7) / w;
      const px = Math.max(0, Math.min(photo.width - 1, Math.round(sx)));
      const py = Math.max(0, Math.min(photo.height - 1, Math.round(sy)));
      const at = (py * photo.width + px) * 4;
      const to = (y * width + x) * 4;
      out.data[to] = pixels[at] as number;
      out.data[to + 1] = pixels[at + 1] as number;
      out.data[to + 2] = pixels[at + 2] as number;
      out.data[to + 3] = 255;
    }
  }
  into.putImageData(out, 0, 0);
  return encode(canvas, 'original');
}
